<template>
  <HrPageLayout
    title="Holiday Calendar"
    subtitle="View holidays in calendar format"
    icon="📅"
    :breadcrumb-items="[{ label: 'Company Settings' }, { label: 'Holidays', route: '/holidays' }, { label: 'Calendar' }]"
  >
    <template #header-actions>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="mdi mdi-refresh"></i> Refresh
      </button>
    </template>

    <div class="container-fluid">
      <!-- Calendar Controls -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-2 mb-md-0">Holiday Calendar</h5>

                      <div class="d-flex align-items-center me-auto">
                        <label class="me-2">Year:</label>
                        <select v-model="selectedYear" class="form-select form-select-sm me-3" style="width: 100px;" @change="updateCalendar">
                          <option v-for="year in yearOptions" :key="year" :value="year">
                            {{ year }}
                          </option>
                        </select>

                        <div class="btn-group me-3" role="group" aria-label="Calendar view options">
                          <button
                              type="button"
                              class="btn btn-sm"
                              :class="calendarView === 'dayGridMonth' ? 'btn-primary' : 'btn-outline-primary'"
                              @click="setCalendarView('dayGridMonth')"
                          >
                            Month
                          </button>
                          <button
                              type="button"
                              class="btn btn-sm"
                              :class="calendarView === 'dayGridYear' ? 'btn-primary' : 'btn-outline-primary'"
                              @click="setCalendarView('dayGridYear')"
                          >
                            Year
                          </button>
                          <button
                              type="button"
                              class="btn btn-sm"
                              :class="calendarView === 'listYear' ? 'btn-primary' : 'btn-outline-primary'"
                              @click="setCalendarView('listYear')"
                          >
                            List
                          </button>
                        </div>
                      </div>

                      <div class="d-flex align-items-center">
                        <div class="form-check form-switch me-3">
                          <input class="form-check-input" type="checkbox" id="showWeekends" v-model="showWeekends" @change="updateCalendarOptions">
                          <label class="form-check-label" for="showWeekends">Show Weekends</label>
                        </div>

                        <div class="dropdown">
                          <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" id="calendarActionsDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                            <i class="mdi mdi-dots-vertical"></i>
                          </button>
                          <ul class="dropdown-menu" aria-labelledby="calendarActionsDropdown">
                            <li><a class="dropdown-item" href="#" @click.prevent="printCalendar"><i class="mdi mdi-printer me-1"></i> Print View</a></li>
                            <li><a class="dropdown-item" href="#" @click.prevent="exportEvents"><i class="mdi mdi-calendar-export me-1"></i> Export to iCal</a></li>
                            <li><a class="dropdown-item" href="#" @click.prevent="refreshData"><i class="mdi mdi-refresh me-1"></i> Refresh</a></li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <!-- Filter pills for categories -->
                    <div class="mb-3">
                      <span class="me-2 text-muted">Filter by Category:</span>
                      <div class="d-inline-flex flex-wrap gap-2 align-items-center">
                        <button
                            class="btn btn-sm"
                            :class="selectedCategories.length === 0 ? 'btn-primary' : 'btn-outline-primary'"
                            @click="clearCategoryFilter"
                        >
                          All
                        </button>
                        <button
                            v-for="category in categories"
                            :key="category.id"
                            class="btn btn-sm category-filter-pill"
                            :class="isCategorySelected(category.id) ? 'active' : ''"
                            :style="{ backgroundColor: isCategorySelected(category.id) ? category.color_code : 'transparent', color: isCategorySelected(category.id) ? 'white' : 'inherit', borderColor: category.color_code }"
                            @click="toggleCategoryFilter(category.id)"
                        >
                          {{ category.category_name }}
                        </button>
                      </div>
                    </div>

                    <!-- Legend -->
                    <div class="d-flex flex-wrap align-items-center text-muted small gap-3">
                      <div><i class="mdi mdi-circle text-success me-1"></i> Public Holiday</div>
                      <div><i class="mdi mdi-circle text-primary me-1"></i> Company Holiday</div>
                      <div><i class="mdi mdi-circle text-warning me-1"></i> Floating Holiday</div>
                      <div v-if="showWeekends"><i class="mdi mdi-square-outline text-muted me-1"></i> Weekend</div>
                      <div><i class="mdi mdi-dots-horizontal text-info me-1"></i> Working Day</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <!-- Main Calendar -->
            <div class="row">
              <div class="col-12 mb-4">
                <div class="card">
                  <div class="card-body p-0">
                    <div class="calendar-container" :class="{ 'calendar-loading': calendarLoading }">
                      <div id="holidays-full-calendar"></div>
                      <div v-if="calendarLoading" class="calendar-loading-overlay">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
    </div>

  <!-- Holiday Detail Modal -->
  <div class="modal fade" id="holidayDetailModal" tabindex="-1" aria-labelledby="holidayDetailModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="holidayDetailModalLabel">Holiday Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedHoliday">
            <div class="mb-4">
              <h4 class="font-size-18">{{ selectedHoliday.title }}</h4>
              <div class="d-flex align-items-center my-2">
                <span class="badge me-2" :style="{ backgroundColor: selectedHoliday.backgroundColor }">
                  {{ selectedHoliday.extendedProps.categories || 'General Holiday' }}
                </span>
                <span class="badge" :class="selectedHoliday.extendedProps.isPublic ? 'bg-success' : 'bg-secondary'">
                  {{ selectedHoliday.extendedProps.isPublic ? 'Public Holiday' : 'Company Holiday' }}
                </span>
              </div>
              <p class="mb-0"><i class="mdi mdi-calendar me-1"></i> {{ formatDate(selectedHoliday.start) }}</p>
            </div>

            <div class="mb-3">
              <h6 class="font-size-14">Description</h6>
              <p>{{ selectedHoliday.extendedProps.description || 'No description available.' }}</p>
            </div>

            <div v-if="selectedHoliday.extendedProps.isFixed" class="alert alert-info">
              <i class="mdi mdi-information-outline me-1"></i>
              This is a fixed holiday that recurs annually.
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button v-if="selectedHoliday" type="button" class="btn btn-primary" @click="navigateToEdit">
            <i class="mdi mdi-pencil me-1"></i> Edit
          </button>
        </div>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import HrPageLayout from "./HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';



export default {
  name: "HolidayCalendar",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      calendarLoading: false,
      selectedYear: new Date().getFullYear(),
      calendarView: 'dayGridMonth',
      showWeekends: false,
      events: [],
      categories: [],
      selectedCategories: [],
      calendar: null,
      selectedHoliday: null,
      workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    };
  },
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear();
      return [
        currentYear - 2,
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2
      ];
    }
  },
  mounted() {
    this.getCategories();
    this.initCalendar();
  },
  methods: {
    getCategories() {
      const data = new FormData();
      data.append("function", "getHolidayCategories");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.categories = res.data.data.categories || [];
              this.fetchData();
            } else {
              console.error("Error fetching categories:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    fetchData() {
      this.loading = true;
      this.calendarLoading = true;

      const data = new FormData();
      data.append("function", "getHolidayCalendar");
      data.append("year", this.selectedYear);
      data.append("view", this.calendarView === 'dayGridMonth' ? 'month' : 'year');

      // Add selected categories if any
      if (this.selectedCategories.length > 0) {
        data.append("categories", JSON.stringify(this.selectedCategories));
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            this.calendarLoading = false;

            if (res.data.success) {
              this.events = res.data.data.events || [];
              this.workingDays = res.data.data.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

              // Update calendar
              if (this.calendar) {
                this.calendar.removeAllEvents();
                this.calendar.addEventSource(this.events);
                this.updateCalendarOptions();
              }
            } else {
              console.error("Error fetching calendar data:", res.data.message);
              this.showToast("error", "Failed to load calendar data: " + res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            this.calendarLoading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching calendar data");
          });
    },

    initCalendar() {
      const calendarEl = document.getElementById('holidays-full-calendar');
      if (!calendarEl) return;

      this.calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin, listPlugin, interactionPlugin, bootstrap5Plugin],
        initialView: this.calendarView,
        themeSystem: 'bootstrap5',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: ''
        },
        events: this.events,
        eventClick: this.handleEventClick,
        eventTimeFormat: {
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short'
        },
        height: 800,
        firstDay: 1, // Monday as first day
        weekends: this.showWeekends,
        hiddenDays: this.getHiddenDays(),
        dayHeaderFormat: { weekday: 'long' }
      });

      this.calendar.render();
    },

    updateCalendar() {
      this.fetchData();

      if (this.calendar) {
        // Update view to the selected year
        this.calendar.gotoDate(`${this.selectedYear}-01-01`);
      }
    },

    updateCalendarOptions() {
      if (!this.calendar) return;

      this.calendar.setOption('weekends', this.showWeekends);
      this.calendar.setOption('hiddenDays', this.getHiddenDays());
    },

    getHiddenDays() {
      // Get days to hide based on working days configuration and weekend display preference
      const allDays = [0, 1, 2, 3, 4, 5, 6]; // Sunday to Saturday (0-6)
      const workingDayIndices = this.getWorkingDayIndices();

      // If showing weekends, only hide non-working days
      // Otherwise, hide both weekends and non-working days
      if (this.showWeekends) {
        return allDays.filter(day => !workingDayIndices.includes(day));
      } else {
        // Weekend days (0 = Sunday, 6 = Saturday)
        const weekendDays = [0, 6];
        // Combined hidden days (both non-working days and weekend days)
        return [...new Set([...allDays.filter(day => !workingDayIndices.includes(day)), ...weekendDays])];
      }
    },

    getWorkingDayIndices() {
      // Convert working day names to day indices (0-6, where 0 is Sunday)
      const dayMap = {
        'Sunday': 0,
        'Monday': 1,
        'Tuesday': 2,
        'Wednesday': 3,
        'Thursday': 4,
        'Friday': 5,
        'Saturday': 6
      };

      return this.workingDays.map(day => dayMap[day]);
    },

    setCalendarView(view) {
      this.calendarView = view;

      if (this.calendar) {
        this.calendar.changeView(view);

        // If changing to year view, fetch all year data
        if (view === 'dayGridYear' || view === 'listYear') {
          this.fetchData();
        }
      }
    },

    handleEventClick(info) {
      this.selectedHoliday = info.event;
      new bootstrap.Modal(document.getElementById('holidayDetailModal')).show();
    },

    navigateToEdit() {
      if (!this.selectedHoliday) return;

      const holidayId = this.selectedHoliday.id;
      // Close modal
      const modal = bootstrap.Modal.getInstance(document.getElementById('holidayDetailModal'));
      if (modal) {
        modal.hide();
      }

      // Navigate to holiday edit page
      this.$router.push(`/holidays/edit/${holidayId}`);
    },

    toggleCategoryFilter(categoryId) {
      const index = this.selectedCategories.indexOf(categoryId);

      if (index > -1) {
        // Remove if already selected
        this.selectedCategories.splice(index, 1);
      } else {
        // Add if not selected
        this.selectedCategories.push(categoryId);
      }

      this.fetchData();
    },

    clearCategoryFilter() {
      this.selectedCategories = [];
      this.fetchData();
    },

    isCategorySelected(categoryId) {
      return this.selectedCategories.includes(categoryId);
    },

    refreshData() {
      this.fetchData();
    },

    printCalendar() {
      window.print();
    },

    exportEvents() {
      // Create iCal format
      let icalContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Company//Holiday Calendar//EN'
      ];

      // Add events
      this.events.forEach(event => {
        const startDate = new Date(event.start);
        const formattedStart = startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

        // End date is start date + 1 for all-day events
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
        const formattedEnd = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

        icalContent.push('BEGIN:VEVENT');
        icalContent.push(`UID:${event.id}@company.com`);
        icalContent.push(`DTSTAMP:${formattedStart}`);
        icalContent.push(`DTSTART:${formattedStart}`);
        icalContent.push(`DTEND:${formattedEnd}`);
        icalContent.push(`SUMMARY:${event.title}`);

        if (event.extendedProps && event.extendedProps.description) {
          icalContent.push(`DESCRIPTION:${event.extendedProps.description}`);
        }

        if (event.extendedProps && event.extendedProps.categories) {
          icalContent.push(`CATEGORIES:${event.extendedProps.categories}`);
        }

        icalContent.push('END:VEVENT');
      });

      icalContent.push('END:VCALENDAR');

      // Download file
      const blob = new Blob([icalContent.join('\r\n')], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `holidays_${this.selectedYear}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  },
  watch: {
    selectedYear: function() {
      this.updateCalendar();
    }
  },
  beforeUnmount() {
    // Clean up calendar instance
    if (this.calendar) {
      this.calendar.destroy();
    }
  }
};
</script>

<style scoped>
.calendar-container {
  position: relative;
  min-height: 800px;
}

.calendar-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.category-filter-pill {
  border-width: 1px;
  border-style: solid;
  transition: all 0.3s;
}

.category-filter-pill:hover:not(.active) {
  opacity: 0.7;
}

.category-filter-pill.active {
  font-weight: 500;
}

/* Print styles */
@media print {
  .page-title-box, .card-body, .page-content {
    padding: 0 !important;
  }

  .modal, .dropdown-menu, .page-title-right, button, select, .legend, nav {
    display: none !important;
  }

  .card, .container-fluid {
    border: none !important;
    box-shadow: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .calendar-container {
    height: 100% !important;
  }
}
</style>